import { Dispatch, SetStateAction, useCallback, useContext, useState } from "react";
import { SortColumn } from "react-data-grid";

import { EditFileBlueIcon } from "../../../../../assets";
import { ProjectActivityReviewsConstants } from "../../../../../constants";
import { CursorChangeProps, ResultType } from "../../../../../models";
import { searchActivityReviews, SearchActivityReviewsRequest } from "../../../../../service/query";
import { FilterGroups, ResultData } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { flattenObject } from "../../../../../utils";
import { getActivityReviewDashboardTabRoute } from "../../../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../../../widget";
import {
  AvatarCellFormatter,
  RemainingTimeCellFormatter,
} from "../../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";
import { ProjectContext } from "../../../../shared/projects/project/ProjectContext";

interface UseActivityReviewReturnData {
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  currentUserRole?: string;
  showAssignActivityModal: boolean;
  selectedActivityReview: unknown;
  setShowAssignActivityModal: Dispatch<SetStateAction<boolean>>;
  setRefreshTables: Dispatch<SetStateAction<boolean>>;
  refreshTables: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useActivityReview = (): UseActivityReviewReturnData => {
  const { currentUserRole } = useAuth();
  const { projectDetails } = useContext(ProjectContext);
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [refreshTables, setRefreshTables] = useState(false);
  const [showAssignActivityModal, setShowAssignActivityModal] = useState(false);
  const [selectedActivityReview, setSelectedActivityReview] = useState<unknown>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      key: "activityLink",
      name: "Activity",
      dataType: "string",
      formatter: "link",
    },
    { key: "type", name: "Type", dataType: "string" },
    {
      key: "status",
      name: "Status",
      dataType: "string",
      formatter: "projectActivityReviewsStatusPill",
      minWidth: 180,
    },
    {
      key: "avatar",
      name: "Assigned to",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: false,
    },
    {
      name: "Progress",
      key: "completionPercentage",
      dataType: "number",
      formatter: "progress",
      minWidth: 140,
      sortable: true,
      filterable: false,
    },
    {
      name: "Days in queue",
      key: "calculatedDaysInQueue",
      dataType: "number",
      filterable: false,
      sortable: false,
      minWidth: 120,
    },
    {
      name: "Deadline",
      key: "deadline",
      dataType: "number",
      formatter: "custom",
      customCellFormatter: RemainingTimeCellFormatter,
      minWidth: 160,
      filterable: false,
      sortable: false,
    },
    {
      key: "editReviewLink",
      name: "Edit review",
      dataType: "string",
      formatter: "icon",
      alignment: "center",
      filterable: false,
      sortable: false,
    },
  ];

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "type", direction: "ASC" }];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatData = useCallback((responseData: any | undefined): ResultData[] => {
    return (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      responseData?.results?.map((d: any) => {
        const result = flattenObject(d);
        const fullName: string = d.primaryAssignedToUser?.fullName ?? undefined;

        result.activityLink = {
          text: d.activity.activityDefinition.displayName,
          to: getActivityReviewDashboardTabRoute(d.activity.uuid, "documents"),
        } as DataGridLinkCellFormatterData;

        if (d.status !== ProjectActivityReviewsConstants.STATUS_CANCELLED) {
          result.editReviewLink = {
            // action: () => open edit activity modal on this review,
            action: () => {
              setSelectedActivityReview(d);
              setShowAssignActivityModal(true);
            },
            icon: <EditFileBlueIcon width={20} height={20} />,
          } as IconCellFormatterData;
        }

        return {
          ...result,
          avatar: { fullName },
          deadline: {
            days: d.calculatedDaysToDeadline,
            isComplete: d.status === ProjectActivityReviewsConstants.STATUS_COMPLETE,
          },
        };
      }) || []
    );
  }, []);

  // eslint-disable-next-line unused-imports/no-unused-vars
  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.status = {
      operator: "neq",
      value: ProjectActivityReviewsConstants.STATUS_CANCELLED,
    };

    if (filterCriteria.activityLink) {
      filterCriteria.activity = {
        activityDefinition: { displayName: filterCriteria.activityLink },
      };
      delete filterCriteria.activityLink;
    }

    sorting.forEach((s) => {
      switch (s.key) {
        case "activityLink":
          // eslint-disable-next-line no-param-reassign
          s.key = "activity.activityDefinition.displayName";
          break;
        case "avatar":
          // eslint-disable-next-line no-param-reassign
          s.key = "assignedToUser.fullName";
          break;
        default:
          break;
      }
    });

    const request: SearchActivityReviewsRequest & FilterGroups<SearchActivityReviewsRequest["filter"]> = {
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 10,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((s: any) => ({
        key: s.key,
        direction: s.direction,
      })),
    };

    // Always filter by project
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let filterResults: any = {
      project: {
        uuid: {
          operator: "eq",
          value: projectDetails?.uuid || null,
        },
      },
    };

    // If in group, filter by group
    if (projectDetails?.group?.groupUuid) {
      filterResults = {
        ...filterResults,
        group: {
          uuid: {
            operator: "eq",
            value: projectDetails?.group?.groupUuid || null,
          },
        },
      };
    }

    request.filterGroups = [
      {
        operator: "or",
        filter: {
          results: filterResults,
        },
      },
    ];

    request.filter = { results: filterCriteria };

    await searchActivityReviews(request)
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  return {
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    currentUserRole,
    showAssignActivityModal,
    selectedActivityReview,
    setShowAssignActivityModal,
    setRefreshTables,
    refreshTables,
    onChange,
  };
};
